/**
 * <s-product-image-zoom> — PDP image zoom: preload + gallery navigation.
 *
 * Gallery buttons open native dialogs declaratively via commandfor/show-modal.
 * This component:
 *   - warms the selected zoom image on hover/focus so the dialog opens sharp;
 *   - lets shoppers step through all product images inside an open zoom dialog
 *     (prev/next buttons, ArrowLeft/ArrowRight, horizontal swipe). Stepping swaps
 *     the image inside the open dialog, so focus and the close control stay put;
 *     each dialog resets to its own image when closed.
 *
 * Required markup:
 *   [data-zoom-preload][commandfor] — gallery trigger button; commandfor points to a dialog id.
 *   dialog img[data-zoom-image]     — high-resolution image inside the target dialog.
 * Optional markup (inside each dialog):
 *   button[data-zoom-step="-1|1"]   — previous / next controls.
 *   [data-zoom-counter]             — receives "current / total".
 */
class SProductImageZoom extends HTMLElement {
  #ac;
  #preloaded = new Set();
  #preloadLinks = [];
  #slides = [];
  #swipeStart = null;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('pointerover', this.#handlePreloadIntent, opts);
    this.addEventListener('focusin', this.#handlePreloadIntent, opts);

    this.#collectSlides();
    this.#initGalleryDots(opts);
    this.addEventListener('click', this.#handleStepClick, opts);
    this.addEventListener('keydown', this.#handleKeydown, opts);
    this.addEventListener('pointerdown', this.#handlePointerDown, opts);
    this.addEventListener('pointerup', this.#handlePointerUp, opts);

    for (const [index, slide] of this.#slides.entries()) {
      slide.dialog.addEventListener('close', () => this.#show(slide.dialog, index), opts);
      this.#updateCounter(slide.dialog, index);
    }
  }

  disconnectedCallback() {
    this.#ac?.abort();

    for (const link of this.#preloadLinks) {
      link.remove();
    }
    this.#preloadLinks = [];
    this.#preloaded.clear();
  }


  /** Mobile gallery: mark the dot of the photo nearest the scroller's centre. */
  #initGalleryDots(opts) {
    const gallery = this.querySelector('[data-product-gallery]');
    const dots = this.querySelector('[data-gallery-dots]');
    if (!gallery || !dots) return;
    const slides = [...gallery.querySelectorAll('[data-gallery-slide]')];
    const marks = [...dots.children];
    if (slides.length < 2) return;

    let frame = 0;
    const update = () => {
      frame = 0;
      const box = gallery.getBoundingClientRect();
      const centre = box.left + box.width / 2;
      let best = 0;
      let bestDistance = Infinity;
      slides.forEach((slide, index) => {
        const r = slide.getBoundingClientRect();
        const distance = Math.abs(r.left + r.width / 2 - centre);
        if (distance < bestDistance) { bestDistance = distance; best = index; }
      });
      marks.forEach((mark, index) => mark.toggleAttribute('data-active', index === best));
    };
    gallery.addEventListener('scroll', () => { if (!frame) frame = requestAnimationFrame(update); }, { ...opts, passive: true });
  }

  #collectSlides() {
    this.#slides = [];
    for (const dialog of this.querySelectorAll('dialog')) {
      const image = dialog.querySelector('img[data-zoom-image]');
      if (!(image instanceof HTMLImageElement)) continue;
      this.#slides.push({
        dialog,
        src: image.getAttribute('src'),
        srcset: image.getAttribute('srcset') || '',
        alt: image.getAttribute('alt') || '',
        width: image.getAttribute('width'),
        height: image.getAttribute('height'),
      });
    }
  }

  #openDialog() {
    return this.#slides.find((slide) => slide.dialog.open)?.dialog ?? null;
  }

  #currentIndex(dialog) {
    const stored = Number(dialog.dataset.zoomIndex);
    if (Number.isInteger(stored) && dialog.dataset.zoomIndex !== undefined) return stored;
    return this.#slides.findIndex((slide) => slide.dialog === dialog);
  }

  #step(dialog, delta) {
    const total = this.#slides.length;
    if (total < 2) return;
    const next = (this.#currentIndex(dialog) + delta + total) % total;
    this.#show(dialog, next);
  }

  #show(dialog, index) {
    const slide = this.#slides[index];
    const image = dialog.querySelector('img[data-zoom-image]');
    if (!slide || !(image instanceof HTMLImageElement)) return;

    image.loading = 'eager';
    if (slide.srcset) image.setAttribute('srcset', slide.srcset);
    else image.removeAttribute('srcset');
    image.setAttribute('src', slide.src);
    image.setAttribute('alt', slide.alt);
    if (slide.width) image.setAttribute('width', slide.width);
    if (slide.height) image.setAttribute('height', slide.height);

    dialog.dataset.zoomIndex = String(index);
    this.#updateCounter(dialog, index);
  }

  #updateCounter(dialog, index) {
    const counter = dialog.querySelector('[data-zoom-counter]');
    if (counter) counter.textContent = `${index + 1} / ${this.#slides.length}`;
  }

  #handleStepClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const control = event.target.closest('[data-zoom-step]');
    if (!control || !this.contains(control)) return;
    const dialog = control.closest('dialog');
    if (!dialog) return;
    event.preventDefault();
    this.#step(dialog, Number(control.getAttribute('data-zoom-step')) || 1);
  };

  #handleKeydown = (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    const dialog = this.#openDialog();
    if (!dialog) return;
    const rtl = getComputedStyle(dialog).direction === 'rtl';
    const forward = (event.key === 'ArrowRight') !== rtl;
    event.preventDefault();
    this.#step(dialog, forward ? 1 : -1);
  };

  #handlePointerDown = (event) => {
    if (event.pointerType === 'mouse') return;
    const dialog = this.#openDialog();
    if (!dialog || !dialog.contains(event.target)) return;
    this.#swipeStart = { x: event.clientX, y: event.clientY };
  };

  #handlePointerUp = (event) => {
    const start = this.#swipeStart;
    this.#swipeStart = null;
    if (!start) return;
    const dialog = this.#openDialog();
    if (!dialog) return;
    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;
    if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
    this.#step(dialog, dx < 0 ? 1 : -1);
  };

  #handlePreloadIntent = (event) => {
    if (!(event.target instanceof Element)) return;

    const source = event.target.closest('[data-zoom-preload]');
    if (!source || !this.contains(source)) return;

    this.#preload(source);
  };

  #preload(source) {
    const dialogId = source.getAttribute('commandfor');
    if (!dialogId || this.#preloaded.has(dialogId)) return;

    const dialog = document.getElementById(dialogId);
    const image = dialog?.querySelector('img[data-zoom-image]');
    if (!(image instanceof HTMLImageElement)) return;

    this.#preloaded.add(dialogId);
    image.loading = 'eager';
    image.fetchPriority = 'high';

    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = image.src;
    if (image.srcset) link.setAttribute('imagesrcset', image.srcset);
    if (image.sizes) link.setAttribute('imagesizes', image.sizes);
    document.head.append(link);
    this.#preloadLinks.push(link);

    if (typeof image.decode === 'function') {
      image.decode().catch(() => {});
    }
  }
}

if (!customElements.get('s-product-image-zoom')) {
  customElements.define('s-product-image-zoom', SProductImageZoom);
}
